import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const slides = [
    {
        title: (
            <>
                The perfect <span className="led-hl">Alternative</span>
                <br />
                to ‘Offline’ Tuition.
            </>
        ),
        kicker: "The online class that finally feels right.",
        note: (
            <>
                Students learn best in environments that feel familiar. InstaED
                recreates the structure, discipline and interaction of a good offline
                classroom — without the distractions or the crowding.
            </>
        ),
    },
    {
        title: (
            <>
                <span className="led-hl">Feels like Classroom.</span>
                <br />
                <em>Works Better.</em>
            </>
        ),
        kicker:
            "Learn from the comfort of your home. No travel, no extra expense, no unsafe commute after dark.",
        note:
            "Fixed batches. A fixed weekly timetable. Thirty students, never more. Everything a good offline class gets right, minus the rickshaw ride and the crowded back bench.",
    },
    {
        title: (
            <>
                <span className="led-hl">Less Edtech.</span>
                <br />
                <em>More Education.</em>
            </>
        ),
        kicker: "Less technology noise. More teaching clarity.",
        note:
            "Most platforms lead with loud tech features, AI and scale. InstaED is built around how real teaching works — clear explanations, continuous interaction, real teachers.",
    },
    {
        title: (
            <>
                <span className="led-hl">Top Teachers.</span>
                <br />
                <em>Thoughtful Pedagogy.</em>
            </>
        ),
        kicker:
            "No AI teachers. Just real ones. AI checks the homework — teachers teach the class.",
        note:
            "Classes are conducted by experienced educators from leading schools, not scripted instructors or recorded voices. Teachers who know where students get stuck, and how to fix it — live.",
    },
    {
        title: (
            <>
                <span className="led-hl">No Boring Lectures.</span>
                <br />
                <em>No Silent Screens.</em>
            </>
        ),
        kicker: "Classes where students can't drift away.",
        note:
            "Classes are designed so students participate, respond and stay mentally present. Teachers notice when a student is quiet or drifting, and step in. Silence is treated as confusion, not attendance.",
    },
    {
        title: (
            <>
                <span className="led-hl">Progress you can feel.</span>
                <br />
                <em>Results you can see.</em>
            </>
        ),
        kicker: "You don't just learn more — you know you've improved.",
        note:
            "Students don't just finish classes — they recognise their own improvement. Outcomes are real, measurable and experienced, not promised.",
    },
];

const LandingPage = ({ className = "" }) => {
    const navigate = useNavigate();
    const [activeSlide, setActiveSlide] = useState(0);
    const timerRef = useRef(null);

    const startCarousel = () => {
        window.clearInterval(timerRef.current);

        timerRef.current = window.setInterval(() => {
            setActiveSlide((current) => (current + 1) % slides.length);
        }, 5200);
    };

    useEffect(() => {
        startCarousel();

        return () => {
            window.clearInterval(timerRef.current);
        };
    }, []);

    const changeSlide = (index) => {
        setActiveSlide(index);
        startCarousel();
    };

    const pauseCarousel = () => {
        window.clearInterval(timerRef.current);
    };

    const resumeCarousel = () => {
        startCarousel();
    };

    const currentSlide = slides[activeSlide];

    return (
        <main
            className={`led-landing ${className}`}
            onMouseEnter={pauseCarousel}
            onMouseLeave={resumeCarousel}
            onFocusCapture={pauseCarousel}
            onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    resumeCarousel();
                }
            }}
        >
            <div className="led-container">
                {/* ================= HEADER ================= */}
                <header className="led-header">
                    <button
                        type="button"
                        className="led-logo"
                        onClick={() => navigate("/")}
                        aria-label="InstaED Tuitions home"
                    >
                        <strong>
                            Insta<span>ED</span> Tuitions
                        </strong>
                        <small>Online tuitions · classes 6–10</small>
                    </button>

                    <nav className="led-header-actions" aria-label="Primary navigation">
                        <button
                            type="button"
                            className="led-btn led-btn-ghost led-btn-sm"
                            onClick={() => navigate("/howtouse")}
                        >
                            How it works
                        </button>

                        <button
                            type="button"
                            className="led-btn led-btn-ghost led-btn-sm"
                            onClick={() => navigate("/login")}
                        >
                            Sign in
                        </button>

                        <button
                            type="button"
                            className="led-btn led-btn-violet led-btn-sm"
                            onClick={() => navigate("/signup")}
                        >
                            Get started
                        </button>
                    </nav>
                </header>

                {/* ================= HERO ================= */}
                <section className="led-splash-body" aria-labelledby="landing-title">
                    <div className="led-hero-copy">
                        <div className="led-hero-rule" />

                        <div className="led-slide" key={`slide-${activeSlide}`}>
                            <h1 id="landing-title">{currentSlide.title}</h1>

                            <p className="led-kicker">{currentSlide.kicker}</p>
                        </div>

                        <div className="led-hero-note" key={`note-${activeSlide}`}>
                            {currentSlide.note}
                        </div>

                        <div className="led-action-row">
                            <button
                                type="button"
                                className="led-btn led-btn-primary"
                                onClick={() => navigate("/signup")}
                            >
                                Get started
                            </button>

                            <button
                                type="button"
                                className="led-btn led-btn-ghost"
                                onClick={() => navigate("/login")}
                            >
                                I have an account
                            </button>

                            <div className="led-dots" aria-label="Hero slides">
                                {slides.map((_, index) => (
                                    <button
                                        type="button"
                                        key={index}
                                        className={`led-dot ${activeSlide === index ? "active" : ""
                                            }`}
                                        aria-label={`Show slide ${index + 1}`}
                                        aria-current={activeSlide === index ? "true" : undefined}
                                        onClick={() => changeSlide(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ================= FACTS ================= */}
                    <div className="led-facts">
                        <div className="led-fact">
                            <b>30</b>
                            <span>students per batch, capped hard</span>
                        </div>

                        <div className="led-fact">
                            <b>4–6 PM</b>
                            <span>Maths and Science, every class day</span>
                        </div>

                        <div className="led-fact">
                            <b>3×</b>
                            <span>a week — MWF or TTS batch</span>
                        </div>

                        <div className="led-fact">
                            <b>100%</b>
                            <span>of classes recorded for revision</span>
                        </div>
                    </div>
                </section>
            </div>

            {/* ================= FOOTER ================= */}
            <footer className="led-footer">
                <div className="led-footer-inner">
                    <button type="button" onClick={() => navigate("/terms-condition")}>
                        Terms of use
                    </button>

                    <button type="button" onClick={() => navigate("/policy")}>
                        Privacy policy
                    </button>

                    <span>© 2026 InstaED Tuitions</span>
                </div>
            </footer>
        </main>
    );
};

export default LandingPage;